import type { ReactNode } from 'react';
import { DeniedState, EmptyState, ErrorState, LoadingSkeleton } from '@/shared/feedback';
import { formatDate } from '@/shared/format';
import { RichTextDisplay } from '@/shared/ui/rich-text-editor';
import {
  describeUnknownAgreementFailure,
  isForbidden,
  isNotFound,
} from '@/modules/agreements/shared';
import { useVersionContent } from '@/modules/agreements/platform/pages/document-detail-page/hooks/use-version-content';
import type { AgreementVersionResponse } from '@/api/schemas/agreements.types';

/**
 * One version, rendered as the record it is.
 *
 * The stored HTML is rendered through `RichTextDisplay`, which re-parses it through
 * the editor's own schema — that parse is the sanitisation; there is no raw HTML
 * injection anywhere on this path. A WITHDRAWN version is still content: it is part
 * of the record, so it renders as published and the withdrawal is stated in words
 * (never by colour alone) and dated.
 */
function VersionArticle({ version }: { version: AgreementVersionResponse }): ReactNode {
  return (
    <article className="space-y-3 rounded-lg border border-border bg-surface-raised p-4">
      <header className="space-y-1">
        <h3 className="text-md font-medium text-content">
          Version {version.version} — {version.title}
        </h3>
        {version.withdrawnAt === null ? null : (
          <p className="text-sm font-medium text-caution">
            Withdrawn on {formatDate(version.withdrawnAt)}. Its content is kept as part of the
            record.
          </p>
        )}
      </header>
      <RichTextDisplay html={version.content} emptyPlaceholder="This version has no content." />
    </article>
  );
}

/**
 * The content of the SELECTED version.
 *
 * The read side of authoring: the operator published HTML, and this is what a subject
 * is bound by. The four states stay distinguishable — a refusal is `DeniedState`
 * (polite, no retry), a failure is `ErrorState` (assertive, retryable), an absent
 * document and an empty history say different things, and pending is a skeleton,
 * never "0 versions".
 *
 * Nothing is rendered when no version is selected: the section's own no-selection
 * copy covers that state, and no read is made.
 */
export function VersionContentPane(props: {
  documentId: string;
  selectedVersionId: string | null;
}): ReactNode {
  const { versions, error, isLoading, reload } = useVersionContent(
    props.documentId,
    props.selectedVersionId,
  );

  if (props.selectedVersionId === null) return null;
  if (isLoading) return <LoadingSkeleton shape="detail" rows={3} label="Loading version content" />;

  if (isForbidden(error)) {
    return (
      <DeniedState
        title="Access restricted"
        message="You do not have access to this document's versions."
      />
    );
  }

  if (isNotFound(error)) {
    return (
      <EmptyState
        title="Document not found"
        description="This document does not exist, or it is no longer available."
      />
    );
  }

  if (error) {
    return (
      <ErrorState
        title="Could not load version content"
        message={describeUnknownAgreementFailure(error)}
        onRetry={reload}
      />
    );
  }

  if (versions.length === 0) {
    return (
      <EmptyState
        title="No versions published yet"
        description="Publish a version to see its content here."
      />
    );
  }

  const version = versions.find((row) => row.versionId === props.selectedVersionId);
  if (!version) {
    // The history loaded but does not name the selected version. Saying "empty"
    // would hide a real disagreement between the page's state and the server's.
    return (
      <ErrorState
        title="Could not load version content"
        message="The selected version was not in this document's history. Reload and try again."
        onRetry={reload}
      />
    );
  }

  return <VersionArticle version={version} />;
}
